import React from 'react';
import Config from '../../../../config';
import Typography from '@material-ui/core/Typography';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Paper from '@material-ui/core/Paper';
import Avatar from '@material-ui/core/Avatar';
import Button from '@material-ui/core/Button';

import BackupIcon from '@material-ui/icons/Backup';
import { Box } from '@material-ui/core';

const useStyles = makeStyles((theme: Theme) => {
  return {
    panelSummaryContent: {
      margin: `${theme.spacing(2.5)}px 0`
    },
    detailsContainer: {
      paddingTop: 0
    },
    stepsContainer: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
      paddingLeft: theme.spacing(7.5),
      paddingRight: theme.spacing(7.5)
    },
    avatar: {
      ...theme.typography.h3,
      height: 48,
      width: 48,
      color: theme.palette.secondary.main,
      backgroundColor: '#F7FBFF',
      alignItems: 'center',
      margin: 'auto',
      marginRight: theme.spacing(2)
    },
    imagePanel: {
      display: 'flex'
    },
    link: {
      textDecoration: 'none'
    },
    videoWrapper: {
      width: '100%',
      maxWidth: '650px',
      maxHeight: 'auto',
      margin: '0 auto'
    },
    videoContainer: {
      width: '100%',
      position: 'relative',
      overflow: 'hidden',
      paddingTop: '56.25%'
    },
    videoiFrame: {
      position: 'absolute',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%'
    },
    buttonContainer: {
      display: 'flex',
      justifyContent: 'center',
      paddingTop: theme.spacing(2)
    },
    downloadButton: {
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1)
    },
    imageContainer: {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 4,
      height: 96,
      width: 96,
      display: 'flex',
      marginRight: theme.spacing(3)
    },
    documentContainer: {
      backgroundColor: '#F7FBFF',
      padding: theme.spacing(4),
      display: 'flex',
      flexDirection: 'column'
    },
    documentHeader: {
      margin: 'auto',
      paddingBottom: theme.spacing(1)
    },
    importLogo: {
      height: 46,
      width: 46,
      margin: 'auto',
      color: theme.palette.secondary.main
    }
  };
});

const OneTimeImportPanel: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation('OneTimeImportPanel');
  const instructionsDownload = `${Config.s3Url}/docs/Bulk_Import_Instructions_v12.pdf`;
  const spreadsheetDownload = `${Config.s3Url}/docs/BulkImportTemplate-7.2.xlsx`;
  const sampleSpreadsheetDownload = `${Config.s3Url}/docs/BulkImportTemplate-7.2-SAMPLE.xlsx`;
  const bulkImportOverviewVideo = `${Config.s3Url}/support/video/PLI-Bulk_Import_Spreadsheet.mp4`;

  return (
    <Grid item key={'one-time-bulk-import'}>
      <ExpansionPanel>
        <ExpansionPanelSummary
          aria-controls="one-time-bulk-import-content"
          id="one-time-bulk-import-content"
          classes={{ content: classes.panelSummaryContent }}
          expandIcon={<ExpandMoreIcon />}
        >
          <Avatar className={classes.avatar}>2</Avatar>
          <Grid container spacing={0} alignItems="center" justify="space-between">
            <Grid item>
              <Typography variant="h3">
                {t('One-time Bulk Import (including Google SSO)')}
              </Typography>
              <Typography variant="subtitle1">
                {t(
                  'Detailed instructions for bulk import spreadsheet. OneRoster 1.1 import also accepted.'
                )}
              </Typography>
            </Grid>
            <Grid item className={classes.imagePanel}>
              <Paper className={classes.imageContainer} elevation={0}>
                <BackupIcon className={classes.importLogo} />
              </Paper>
            </Grid>
          </Grid>
        </ExpansionPanelSummary>
        <ExpansionPanelDetails className={classes.detailsContainer}>
          <Grid container spacing={0} direction="column">
            <Grid item>
              <Divider />
            </Grid>
            <Grid item>
              <Grid container spacing={3} className={classes.stepsContainer}>
                <Grid item xs={12}>
                  <Paper className={classes.documentContainer} elevation={0}>
                    <Typography className={classes.documentHeader} variant="h3">
                      {t('Bulk Import Spreadsheet Resources')}
                    </Typography>
                    <Box className={classes.videoWrapper}>
                      <Box className={classes.videoContainer}>
                        <video
                          id="video-iframe"
                          title={t('Bulk Import Overview Video')}
                          className={classes.videoiFrame}
                          src={bulkImportOverviewVideo}
                          controls
                        ></video>
                      </Box>
                    </Box>
                    <Box className={classes.buttonContainer}>
                      <a
                        href={instructionsDownload}
                        className={classes.link}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Button
                          variant="contained"
                          color="primary"
                          disableElevation
                          className={classes.downloadButton}
                        >
                          {t('Bulk Import Instructions')}
                        </Button>
                      </a>
                      <a
                        href={spreadsheetDownload}
                        className={classes.link}
                        rel="noopener noreferrer"
                      >
                        <Button
                          variant="contained"
                          color="primary"
                          disableElevation
                          className={classes.downloadButton}
                        >
                          {t('Bulk Import Spreadsheet')}
                        </Button>
                      </a>
                      <a
                        href={sampleSpreadsheetDownload}
                        className={classes.link}
                        rel="noopener noreferrer"
                      >
                        <Button
                          variant="contained"
                          color="primary"
                          disableElevation
                          className={classes.downloadButton}
                        >
                          {t('Sample Spreadsheet')}
                        </Button>
                      </a>
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h3">{t('Step 1')}</Typography>
                  <Typography variant="subtitle1">
                    {t(
                      'Read the Instructions for Bulk Import and complete the Bulk Import spreadsheet. Or, export your data into OneRoster 1.1 format directly from your Student Information System (SIS).'
                    )}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h3">{t('Step 2')}</Typography>
                  <Typography variant="subtitle1">
                    {t('When files are complete, send it to us using our secure link, ')}
                    <a href="https://upload.lwtears.com/" target="_blank" rel="noopener noreferrer">
                      https://upload.Iwtears.com/
                    </a>
                    {t(' or ')}
                    <a href={Config.rosteringSupportUrl} target="_blank" rel="noopener noreferrer">
                      {t('contact us')}
                    </a>
                    {t(' with any questions.')}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="h3">{t('Step 3')}</Typography>
                  <Typography variant="subtitle1">
                    {t(
                      'We will upload your data. Once licenses have been assigned, we will notify you by email with next steps.'
                    )}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </ExpansionPanelDetails>
      </ExpansionPanel>
    </Grid>
  );
};

export default OneTimeImportPanel;
